import { watch, type Ref } from "vue";
import { DS_LIGHT_BOX_CLASSES } from "../constants/classes";
import {
  DS_LIGHT_BOX_KEYBOARD_EXCLUSION_SELECTOR,
  DS_LIGHT_BOX_POINTER_EXCLUSION_SELECTOR,
} from "../constants/selectors";

const matchesControl = (target: EventTarget | null, selector: string) =>
  target instanceof Element && Boolean(target.closest(selector));
const isInteractive = (target: EventTarget | null) => matchesControl(target, DS_LIGHT_BOX_POINTER_EXCLUSION_SELECTOR);
const isOutsideImage = (image: HTMLImageElement, x: number, y: number) => {
  const { left, top, width, height } = image.getBoundingClientRect();
  const scale = Math.min(width / image.naturalWidth, height / image.naturalHeight);
  if (!Number.isFinite(scale)) return false;
  return (
    Math.abs(x - left - width / 2) > (image.naturalWidth * scale) / 2 ||
    Math.abs(y - top - height / 2) > (image.naturalHeight * scale) / 2
  );
};
const isBackdrop = (target: EventTarget | null, x: number, y: number) => {
  if (target instanceof HTMLImageElement) return isOutsideImage(target, x, y);
  return (
    target instanceof Element && target.matches(`.${DS_LIGHT_BOX_CLASSES.Viewport}, .${DS_LIGHT_BOX_CLASSES.Artwork}`)
  );
};

export const useLightBoxNavigation = ({
  itemKey,
  next,
  previous,
  close,
}: {
  itemKey: Ref<string>;
  next: () => void;
  previous: () => void;
  close: () => void;
}) => {
  let dragStart: { x: number; y: number; id: number; backdrop: boolean } | undefined;
  const cancelDrag = () => {
    dragStart = undefined;
  };
  watch(itemKey, cancelDrag);

  const navigateKey = (event: KeyboardEvent) => {
    const action = ({ ArrowLeft: previous, ArrowRight: next } as Record<string, (() => void) | undefined>)[event.key];
    if (!action) return;
    event.preventDefault();
    action();
  };
  const onKeydown = (event: KeyboardEvent) => {
    if (event.defaultPrevented) return;
    if (matchesControl(event.target, `.${DS_LIGHT_BOX_CLASSES.Actions}`)) return;
    if (matchesControl(event.target, DS_LIGHT_BOX_KEYBOARD_EXCLUSION_SELECTOR)) return;
    navigateKey(event);
  };
  const onPointerDown = (event: PointerEvent) => {
    cancelDrag();
    if (event.button !== 0 || isInteractive(event.target)) return;
    dragStart = {
      x: event.clientX,
      y: event.clientY,
      id: event.pointerId,
      backdrop: isBackdrop(event.target, event.clientX, event.clientY),
    };
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
  };
  const navigateDrag = (dx: number, dy: number) => {
    if (Math.abs(dx) < 50 || Math.abs(dx) <= Math.abs(dy)) return;
    if (dx < 0) next();
    else previous();
  };
  const finishGesture = (dx: number, dy: number, backdrop: boolean) => {
    if (backdrop && Math.hypot(dx, dy) < 10) close();
    else navigateDrag(dx, dy);
  };
  const onPointerUp = (event: PointerEvent) => {
    const start = dragStart;
    cancelDrag();
    if (!start || start.id !== event.pointerId || isInteractive(event.target)) return;
    finishGesture(event.clientX - start.x, event.clientY - start.y, start.backdrop);
  };
  const onPointerCancel = () => cancelDrag();
  return { onKeydown, onPointerDown, onPointerUp, onPointerCancel };
};
